import { Outlet, useLocation } from 'react-router';
import { MessageSquare, Activity, Check } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/lib/theme';
import { Logo, ForgeAlphaIcon } from '@/components/ui/logo';

// ─── Matrix Code Rain (trading-themed) ──────────────────────────────────────
const KEYWORDS = [
  'BUY', 'SELL', 'RSI', 'SMA', 'EMA', 'ATR', 'MACD', 'CLOSE',
  'grid', 'profit', 'equity', 'onTick', 'strategy', 'trailing',
  'XAUUSD', 'EURUSD', 'balance', 'volume', 'signal', 'OPEN',
  'margin', 'hedge', 'spread', 'stoploss', 'takeprofit', 'lot',
  'pip', 'drawdown', 'backtest', 'deploy', 'forge', 'analyze',
];

function MatrixRain({ isDark }: { isDark: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef(0);
  const columnsRef = useRef<{ y: number; speed: number; chars: string[]; keyword: string; kwY: number; kwSpeed: number }[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const fontSize = 13;
    const colWidth = fontSize * 1.2;
    let w = 0;
    let h = 0;

    const bgClear = isDark ? 'rgba(10, 10, 12, 0.12)' : 'rgba(250, 250, 249, 0.12)';
    const bgFull = isDark ? 'rgba(10, 10, 12, 1)' : 'rgba(250, 250, 249, 1)';

    function initColumns() {
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;

      const numCols = Math.floor(w / colWidth);
      columnsRef.current = Array.from({ length: numCols }, (_, i) => ({
        y: Math.random() * h,
        speed: 0.3 + Math.random() * 0.8,
        chars: Array.from({ length: Math.ceil(h / fontSize) + 5 }, () =>
          Math.random() > 0.5 ? '0' : '1'
        ),
        keyword: KEYWORDS[i % KEYWORDS.length]!,
        kwY: Math.random() * h * 0.6,
        kwSpeed: 0.15 + Math.random() * 0.3,
      }));
    }

    initColumns();

    function draw() {
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Fade trail
      ctx.fillStyle = bgClear;
      ctx.fillRect(0, 0, w, h);

      ctx.font = `${fontSize}px "SF Mono", "Fira Code", "Cascadia Code", monospace`;

      for (const col of columnsRef.current) {
        const x = columnsRef.current.indexOf(col) * colWidth;

        // Binary digits — dim orange
        const charsToShow = 15;
        for (let j = 0; j < charsToShow; j++) {
          const charY = col.y - j * fontSize;
          if (charY < -fontSize || charY > h + fontSize) continue;

          const fade = 1 - j / charsToShow;
          const alpha = fade * (isDark ? 0.25 : 0.15);
          ctx.fillStyle = `rgba(249, 115, 22, ${alpha})`;

          // Occasionally mutate a char
          if (Math.random() < 0.02) {
            col.chars[j % col.chars.length] = Math.random() > 0.5 ? '0' : '1';
          }
          ctx.fillText(col.chars[j % col.chars.length]!, x, charY);
        }

        // Head character — bright
        ctx.fillStyle = isDark ? 'rgba(251, 146, 60, 0.7)' : 'rgba(249, 115, 22, 0.5)';
        ctx.fillText(Math.random() > 0.5 ? '0' : '1', x, col.y);

        col.y += col.speed;
        if (col.y > h + charsToShow * fontSize) {
          col.y = -fontSize * 2;
        }

        // Keyword overlay — brighter, floats independently
        ctx.fillStyle = isDark ? 'rgba(249, 115, 22, 0.12)' : 'rgba(249, 115, 22, 0.08)';
        ctx.fillText(col.keyword, x, col.kwY);
        col.kwY += col.kwSpeed;
        if (col.kwY > h + fontSize) {
          col.kwY = -fontSize * 2;
          col.keyword = KEYWORDS[Math.floor(Math.random() * KEYWORDS.length)]!;
        }
      }

      animRef.current = requestAnimationFrame(draw);
    }

    // Initial full clear
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = bgFull;
      ctx.fillRect(0, 0, w, h);
    }

    animRef.current = requestAnimationFrame(draw);

    const handleResize = () => initColumns();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  );
}

// ─── Steps & Features ────────────────────────────────────────────────────────
const steps = [
  {
    icon: MessageSquare,
    label: 'Describe',
    desc: 'Tell AI your trading idea in plain English',
    color: 'text-orange',
    bg: 'bg-orange/10',
    border: 'border-orange/20',
  },
  {
    icon: 'forge-logo' as any,
    label: 'Forge',
    desc: 'AI builds rules, indicators & risk controls',
    color: 'text-amber',
    bg: 'bg-amber/10',
    border: 'border-amber/20',
  },
  {
    icon: Activity,
    label: 'Deploy',
    desc: '24/7 automated trading — live or paper',
    color: 'text-green',
    bg: 'bg-green/10',
    border: 'border-green/20',
  },
];

const featureList = [
  'Natural Language Rules',
  'AI-Powered Suggestions',
  'Historical Backtesting',
  'Risk-Free Paper Trading',
  '74 Technical Indicators',
  'Auto Capital Protection',
];

const stats = [
  { value: '74', label: 'Built-in Indicators' },
  { value: '0', label: 'Lines of Code' },
  { value: '24/7', label: 'Autopilot' },
];

function PromotionalPanel({ isSignup, isDark }: { isSignup: boolean; isDark: boolean }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  const overlayBg = isDark
    ? 'radial-gradient(ellipse 100% 80% at 50% 50%, rgba(10,10,12,0.85) 0%, rgba(10,10,12,0.6) 50%, rgba(10,10,12,0.3) 100%)'
    : 'radial-gradient(ellipse 100% 80% at 50% 50%, rgba(250,250,249,0.85) 0%, rgba(250,250,249,0.6) 50%, rgba(250,250,249,0.3) 100%)';

  return (
    <div className="relative h-full">
      {/* Matrix background */}
      <MatrixRain isDark={isDark} />

      {/* Gradient overlay so text is readable */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: overlayBg }}
      />

      {/* Content — vertically centered */}
      <div className="relative z-10 flex flex-col justify-center h-full px-10 xl:px-16 py-12">
        <div className="max-w-lg mx-auto w-full">
          {isSignup ? (
            <>
              {/* Signup: pitch new users */}
              <h2 className="text-3xl xl:text-4xl font-extrabold tracking-tight leading-tight mb-3">
                Describe Your Strategy.<br />AI <span className="bg-gradient-to-r from-orange to-amber bg-clip-text text-transparent">Builds the Bot.</span>
              </h2>
              <p className="text-base text-ink-3 mb-8 leading-relaxed">
                Algorithmic Trading, Forged by Data. Describe your idea — AI builds, backtests, and deploys it live.
              </p>

              {/* 3-step flow */}
              <div className="space-y-3 mb-8">
                {steps.map((step, i) => (
                  <div
                    key={step.label}
                    className="flex items-start gap-3.5"
                    style={{
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'translateY(0)' : 'translateY(12px)',
                      transition: `opacity 0.5s ease ${i * 0.15}s, transform 0.5s ease ${i * 0.15}s`,
                    }}
                  >
                    <div className={`w-9 h-9 rounded-lg ${step.bg} border ${step.border} flex items-center justify-center shrink-0 backdrop-blur-sm ${step.icon === 'forge-logo' ? 'animate-forge-heat' : ''}`}>
                      {step.icon === 'forge-logo'
                        ? <ForgeAlphaIcon size={18} strokeWidth={10} />
                        : <step.icon size={16} className={step.color} />
                      }
                    </div>
                    <div className="pt-0.5">
                      <div className="text-sm font-bold tracking-wide">{step.label}</div>
                      <div className="text-xs text-ink-3 mt-0.5">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature checklist */}
              <div
                className="grid grid-cols-2 gap-x-6 gap-y-2.5"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: 'opacity 0.5s ease 0.6s',
                }}
              >
                {featureList.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <Check size={14} className="text-green shrink-0" />
                    <span className="text-sm text-ink-2">{f}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Login: welcome back + stats */}
              <h2 className="text-3xl xl:text-4xl font-extrabold tracking-tight leading-tight mb-3">
                Welcome <span className="bg-gradient-to-r from-orange to-amber bg-clip-text text-transparent">Back.</span>
              </h2>
              <p className="text-base text-ink-3 mb-10 leading-relaxed">
                Your AI agents are still running. Pick up where you left off.
              </p>

              {/* Stats */}
              <div
                className="flex gap-8 mb-10"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(12px)',
                  transition: 'opacity 0.5s ease 0.15s, transform 0.5s ease 0.15s',
                }}
              >
                {stats.map((s) => (
                  <div key={s.label}>
                    <div className="text-2xl font-extrabold font-mono text-orange">{s.value}</div>
                    <div className="text-xs text-ink-3 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Feature checklist */}
              <div
                className="grid grid-cols-2 gap-x-6 gap-y-2.5"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: 'opacity 0.5s ease 0.4s',
                }}
              >
                {featureList.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <Check size={14} className="text-green shrink-0" />
                    <span className="text-sm text-ink-2">{f}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AuthLayout() {
  const location = useLocation();
  const isSignup = location.pathname === '/signup';
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const panelBg = isDark ? 'rgba(10,10,12,1)' : 'rgba(250,250,249,1)';
  const borderGradient = isDark
    ? 'linear-gradient(to bottom, transparent, rgba(249,115,22,0.15), transparent)'
    : 'linear-gradient(to bottom, transparent, rgba(249,115,22,0.1), transparent)';

  const tabBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const tabBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left — promotional panel (hidden on mobile) */}
      <div
        className="hidden lg:flex relative overflow-hidden"
        style={{
          flex: '0 0 55%',
          background: panelBg,
        }}
      >
        {/* Glass border on right */}
        <div
          className="absolute right-0 top-0 bottom-0 w-px z-20"
          style={{ background: borderGradient }}
        />
        <div className="relative w-full h-full">
          <PromotionalPanel isSignup={isSignup} isDark={isDark} />
        </div>
      </div>

      {/* Right — auth form */}
      <div
        className="flex-1 flex items-start justify-center p-6 lg:p-12 overflow-y-auto relative"
        style={{ paddingTop: 'max(2rem, calc(50vh - 320px))' }}
      >
        {/* Matrix background — mobile only */}
        <div className="absolute inset-0 lg:hidden">
          <MatrixRain isDark={isDark} />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: isDark
                ? 'radial-gradient(ellipse 100% 80% at 50% 30%, rgba(10,10,12,0.9) 0%, rgba(10,10,12,0.7) 60%, rgba(10,10,12,0.4) 100%)'
                : 'radial-gradient(ellipse 100% 80% at 50% 30%, rgba(250,250,249,0.9) 0%, rgba(250,250,249,0.7) 60%, rgba(250,250,249,0.4) 100%)',
            }}
          />
        </div>
        <div className="w-full max-w-sm relative z-10">
          {/* Logo (visible on all screens) */}
          <div className="flex items-center mb-10 justify-center lg:justify-start">
            <Logo size="lg" />
          </div>

          {/* Tab toggle */}
          <div
            className="flex rounded-lg p-1 mb-6"
            style={{
              background: tabBg,
              border: tabBorder,
            }}
          >
            {([
              { path: '/login', label: 'Sign In' },
              { path: '/signup', label: 'Sign Up' },
            ] as const).map((t) => {
              const active = t.path === '/signup' ? isSignup : !isSignup;
              return (
                <a
                  key={t.path}
                  href={t.path}
                  className="flex-1 text-xs font-bold py-2 rounded-md transition-all duration-200 text-center"
                  style={{
                    background: active ? 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(245,158,11,0.08))' : 'transparent',
                    color: active ? '#f97316' : 'var(--color-ink-3)',
                    border: active ? '1px solid rgba(249,115,22,0.2)' : '1px solid transparent',
                  }}
                >
                  {t.label}
                </a>
              );
            })}
          </div>

          {/* Form content (Login or Signup) */}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
